import { Table } from '@app/domain/Table'
import { v4 as uuid } from 'uuid'

export class VenueClosure {

    constructor(
        public id: string,
        public start: Date,
        public end: Date,
        public tableIds: string[],
        public reasonIds: string[],
        public closes: boolean,
        public description: string | null,
        public dateCreated: Date
    ) { }

    get appliesToEntireVenue() {
        return this.tableIds.length === 0
    }

    occursBetweenDatesOnTable(start: Date, end: Date, table: Table) {
        return this.occursBetweenDates(start, end) && this.occursOnTable(table)
    }

    occursBetweenDates(start: Date, end: Date) {
        return !(this.end <= start || this.start >= end)
    }

    containsDateRange(start: Date, end: Date) {
        return this.start <= start && this.end >= end
    }
    
    occursOnTable(table: Table) {
        return this.appliesToEntireVenue || this.tableIds.includes(table.id)
    }
    
    conflictsWith(other: VenueClosure): boolean {
        if (!this.occursBetweenDates(other.start, other.end)) {
            return false
        }
        if (this.appliesToEntireVenue && other.appliesToEntireVenue) {
            return true
        }
        if (this.appliesToEntireVenue || other.appliesToEntireVenue) {
            return true
        }
        return this.tableIds.some(id => other.tableIds.includes(id))
    }

    isAdjacentTo(other: VenueClosure): boolean {
        if (this.closes !== other.closes) {
            return false
        }
        if (!this.appliesToEntireVenue || !other.appliesToEntireVenue) {
            return false
        }
        const thisEndsWhereOtherStarts = Math.abs(this.end.getTime() - other.start.getTime()) < 1000
        const thisStartsWhereOtherEnds = Math.abs(this.start.getTime() - other.end.getTime()) < 1000
        return thisEndsWhereOtherStarts || thisStartsWhereOtherEnds;
    }

    mergeWith(other: VenueClosure): VenueClosure | null {
        if (this.closes !== other.closes || !this.appliesToEntireVenue || !other.appliesToEntireVenue) {
            return null;
        }
        if (!this.isAdjacentTo(other) && !this.occursBetweenDates(other.start, other.end)) {
            return null;
        }
        return new VenueClosure(
            uuid(),
            new Date(Math.min(this.start.getTime(), other.start.getTime())),
            new Date(Math.max(this.end.getTime(), other.end.getTime())),
            [],
            [],
            this.closes,
            this.description ?? other.description ?? null,
            new Date()
        );
    }
    
    extendToInclude(start: Date, end: Date): VenueClosure {
        return new VenueClosure(
            this.id,
            new Date(Math.min(this.start.getTime(), start.getTime())),
            new Date(Math.max(this.end.getTime(), end.getTime())),
            this.tableIds,
            this.reasonIds,
            this.closes,
            this.description,
            this.dateCreated
        )
    }
    
    static createVenueWideClosure(start: Date, end: Date, closes: boolean = true): VenueClosure {
        return new VenueClosure(
            uuid(),
            start,
            end,
            [],
            [],
            closes,
            null,
            new Date()
        )
    }
}
